// The page for Microsoft's 6502 BASIC (web/microsoft-basic/index.html, and the site's /microsoft-basic/):
// the source fetched and assembled in the browser, each time, and run as Commodore's build on a
// PET's screen in moonbase's characters (../c64/chargen.js), as Apple's on an Apple II's text
// screen or as Ohio Scientific's on a terminal, both in moonbase's font (../font.js), at about a
// million cycles a second, as theirs ran.
// The keys go to the machine; Esc is the break (RUN/STOP, Control-C); Reset a warm start, the
// program kept, and switching off and on a cold one; a paste is typed in.
import { machine } from './machine.js';
import { chargen } from '@moonbase-machines/charset/chargen.js';
import { FONT } from '@moonbase-machines/charset/font.js';
import { watch, diagram } from './chip.js';
import { OPS, SIZE, operand } from '@moonbase-machines/mos6502/disasm.js';
import { programDisk } from '@moonbase-machines/player/disk/disk.js';
import { provider } from '../provider.js';

const root = document.getElementById('msbasic');
// (Microsoft's source where every page has it, the site's /microsoft-basic/vendor/, wherever this module is)
const source = await (await fetch('/microsoft-basic/vendor/m6502.asm')).text();
const CHARS = chargen();
// (the machine chosen, a setting of the person's, kept through the site's provider, web/provider.js)
const MACHINE = { name: 'microsoft-basic-machine', type: ['pet', 'apple', 'osi', 'kim', 'pdp10'], default: 'pet', owner: 'person', words: 'Which build' };
const site = await provider();
const kept = () => site.settings.get(MACHINE);
const keep = v => site.settings.set(MACHINE, v);

root.innerHTML = `
<p class="ms-builds" role="group" aria-label="Which build">
  <button type="button" data-kind="apple">Apple II</button>
  <button type="button" data-kind="pet">Commodore PET</button>
  <button type="button" data-kind="osi">Ohio Scientific</button>
  <button type="button" data-kind="kim">KIM-1</button>
  <button type="button" data-kind="pdp10">PDP-10</button>
</p>
<div class="ms-machine">
<div class="ms-left">
<div class="ms-screen ms-card"><canvas tabindex="0" aria-label="The machine's screen: click, then type"></canvas>
<input class="ms-keys" autocapitalize="characters" autocomplete="off" autocorrect="off" spellcheck="false" aria-label="Keys for the machine"></div>
<section class="ms-strip ms-card" aria-live="polite">
<pre class="ms-ddt" hidden></pre>
<div class="ms-about">
<h3 class="ms-ddt-title" hidden>DDT</h3>
<p class="ms-ddt-state" hidden></p>
<p class="ms-testing"></p>
<p class="ms-help"></p>
<p class="ms-controls"><span class="ms-ddt-buttons" hidden><button type="button" data-ddt="step">Step</button> <button type="button" data-ddt="statement">To the next statement</button> <button type="button" data-ddt="go">Continue</button></span> <button type="button" data-do="stop">Break (Esc)</button> <button type="button" data-do="reset">Reset</button> <button type="button" data-do="restart">Switch it off and on</button></p>
</div>
</section>
</div>
<figure class="ms-chip ms-card"><div></div><figcaption>The 6502 as BASIC runs it: each part lit by its share of the last sixtieth of a second's instructions; the registers and flags as it leaves them, and the next instruction, where it stands.</figcaption></figure>
</div>`;
const panel = diagram(root.querySelector('.ms-chip div'));
let chip;
const canvas = root.querySelector('canvas'), ctx = canvas.getContext('2d'), keys = root.querySelector('.ms-keys'), help = root.querySelector('.ms-help'), testing = root.querySelector('.ms-testing');
const HELP = {
  pet: "Commodore's build, on a PET's screen of 40 columns by 25 rows: type as on a PET, the arrow keys moving the cursor anywhere on the screen, so a line LISTed can be changed and taken in again with Return. SAVE \"NAME\" and LOAD \"NAME\" keep programs in this browser, and LOAD \"$\" then LIST shows them. WAIT 6502,1 writes MICROSOFT! at the top of the screen; WAIT 6502,9 nine times.",
  apple: "Apple's build, on the Apple II's text screen of 40 columns by 24 rows, asks MEMORY SIZE? and TERMINAL WIDTH? first, and shows nothing for an answer of Return alone: at MEMORY SIZE? it means find the memory, which BASIC does by testing it a byte at a time, a second or so before the next question, BYTES FREE saying what it found; at TERMINAL WIDTH? it keeps 40 columns. A number at MEMORY SIZE? is the top of memory, not what is free: try 16384, or 32768. Type A at MEMORY SIZE? for its authors. SAVE writes the program to a tape kept in this browser, and LOAD reads it back. WAIT 6502 is Commodore's alone: here WAIT 6502,1 just says OK, and WAIT 6502,2 never finishes, and Break cannot stop it: press Reset, as on the Apple, and your program is kept.",
  kim: "MOS Technology's KIM-1, on a teletype of 72 columns: the build that fits in 8K, 8,190 bytes. It asks MEMORY SIZE? and WIDTH? first, and shows nothing for an answer of Return alone: at MEMORY SIZE? BASIC finds its memory by testing it a byte at a time, a second or so before the next question; a number there is the top of memory, not what is free: try 32768. SAVE writes the program to a tape kept in this browser, and LOAD reads it back. WAIT 6502 is Commodore's alone: here any WAIT 6502 never finishes, and Break cannot stop it: press Reset, as on the machine, and your program is kept.",
  pdp10: "The PDP-10's build, where the 6502 was itself a program on DEC's mainframe, as it is here: SIMULATED BASIC FOR THE 6502. It asks MEMORY SIZE? and TERMINAL WIDTH? first: press Return for each, or type A at MEMORY SIZE? for its authors. Then try the statement only this build has, DDT, in a program: 10 PRINT \"BEFORE\":DDT:PRINT \"AFTER\". It stops the 6502, as it stopped Microsoft's in the PDP-10's debugger, and here, beside its instructions, you can step through BASIC itself. Esc is the PDP-10's Control-C: back to OK, the program kept.",
  osi: "Ohio Scientific's build, on a terminal of 72 columns, asks MEMORY SIZE? and TERMINAL WIDTH? first, and shows nothing for an answer of Return alone: at MEMORY SIZE? it means find the memory, which BASIC does by testing it a byte at a time, a second or so before the next question, BYTES FREE saying what it found; at TERMINAL WIDTH? it keeps 72 columns. A number at MEMORY SIZE? is the top of memory, not what is free: try 32768. Type A at MEMORY SIZE? for its authors. As on a terminal of the day, _ takes back a character and @ the whole line. SAVE \"NAME\" and LOAD \"NAME\" keep programs in this browser. WAIT 6502 is Commodore's alone: here any WAIT 6502 never finishes, and Break cannot stop it: press Reset, as on the machine, and your program is kept.",
};

// the screen: the PET's green on black, its characters from moonbase's character ROM, 8 by 8;
// the Apple's white on black and the terminal's ink on paper, in moonbase's 5 by 7 font, in the
// Apple's cell of 7 by 8 and the terminal's of 6 by 10
const CELL = { pet: [8, 8], apple: [7, 8], osi: [6, 10], kim: [6, 10], pdp10: [6, 10] };

// what each machine's SAVE keeps, each on a player's disk of its own (@moonbase-machines/player/disk/disk.js): the PET's disk
// and Ohio Scientific's programs by name, the Apple's and the KIM-1's tapes
const openDisk = site.disk;   // (its disks the site's: web/provider.js)
const diskFor = k => openDisk(programDisk(`microsoft-basic-${k}-${k === 'apple' || k === 'kim' ? 'tape' : 'disk'}`));

let m, kind;
async function start(k) {
  const disk = await diskFor(k);
  kind = k; keep(k);
  m = machine(source, k, { disk });
  chip = watch(m.cpu, m.cpu.m.read);
  for (const b of root.querySelectorAll('[data-kind]')) b.setAttribute('aria-pressed', b.dataset.kind === k);
  help.textContent = HELP[k];
  size();
  canvas.dataset.kind = k;
}
function size() {
  const c = m.cells(), [cw, ch] = CELL[kind];
  canvas.width = c.cols * cw + 16; canvas.height = c.rows * ch + 16;
  image = ctx.createImageData(canvas.width, canvas.height);
}
let image;
await start(kept());

// a character's cell as dots, 1 for ink, kept once made
const glyphs = new Map();
function glyph(code) {
  const key = kind + code;
  if (glyphs.has(key)) return glyphs.get(key);
  const [cw, ch] = CELL[kind], dots = new Uint8Array(cw * ch);
  if (kind === 'pet') for (let r = 0; r < 8; r++) for (let b = 0; b < 8; b++) dots[r * 8 + b] = CHARS[code * 8 + r] >> (7 - b) & 1;
  else {
    // (the Apple's glyph one dot in, as its own characters sit; the terminal's one dot down)
    const g = FONT[code - 32], dx = kind === 'apple' ? 1 : 0, dy = kind === 'apple' ? 0 : 1;
    if (g) for (let col = 0; col < 5; col++) for (let r = 0; r < 7; r++) if (g[col] >> r & 1) dots[(dy + r) * cw + dx + col] = 1;
  }
  glyphs.set(key, dots);
  return dots;
}
const rgba = hex => { const h = hex.replace('#', ''), f = h.length === 3 ? [...h].map(c => c + c).join('') : h; const v = parseInt(f, 16); return 0xff000000 | (v & 0xff) << 16 | (v >> 8 & 0xff) << 8 | v >> 16; };
// (a colour as the browser gives it, rgb(42, 38, 34), as a pixel; one it could not give, transparent, as the
// light terminal's, `or`)
const painted = (css, or) => { const [r, g, b, a = 1] = (css.match(/[\d.]+/g) ?? []).map(Number); return css && +a ? 0xff000000 | b << 16 | g << 8 | r : rgba(or); };

// the screen, painted into one image, each cell from its glyph, the cursor's reversed
let blink = 0;
function draw() {
  const c = m.cells(), [cw, ch] = CELL[kind];
  // (the terminal's ink and paper as the page's stylesheet gives them, light or dark, page.css)
  const style = getComputedStyle(canvas);
  const [ink, paper] = kind === 'pet' ? ['#7cff9a', '#06140a'].map(rgba) : kind === 'apple' ? ['#f2f2f2', '#000000'].map(rgba)
    : [painted(style.color, '#2a2622'), painted(style.backgroundColor, '#f6f1e3')];
  const W = canvas.width, px = new Uint32Array(image.data.buffer);
  px.fill(paper);
  const on = (blink >> 4) & 1, gw = cw, gh = ch;
  for (let y = 0; y < c.rows; y++) for (let x = 0; x < c.cols; x++) {
    let { code, reverse } = c.at(x, y);
    if (on && x === c.cursor.col && y === c.cursor.row) reverse = !reverse;
    const g = glyph(code), o = (8 + y * gh) * W + 8 + x * gw;
    for (let r = 0; r < gh; r++) for (let b = 0; b < gw; b++) if (g[r * gw + b] ^ (reverse ? 1 : 0)) px[o + r * W + b] = ink;
  }
  ctx.putImageData(image, 0, 0);
}

// DDT, the PDP-10 build's debugger: the next instructions from the program counter, each under
// its name in the source where it has one, a jump's or a call's target named too
const ddtList = root.querySelector('.ms-ddt'), strip = root.querySelector('.ms-strip');
function listing() {
  const out = [];
  for (let pc = m.cpu.pc, n = 0; out.length < 12; n++) {
    const name = m.label(pc);
    if (name && !name.includes('+')) out.push(`${name}:`);
    const op = OPS[m.ram[pc]], size = op.name === '???' ? 1 : SIZE[op.mode], bytes = m.ram.subarray(pc, pc + size);
    let arg = op.name === '???' ? '' : operand(op.mode, bytes, pc);
    const target = /^\$([0-9A-F]{4})$/.exec(arg);
    if (target && ['abs', 'rel'].includes(op.mode)) { const t = m.label(parseInt(target[1], 16)); if (t) arg = t; }
    out.push(`${n ? '  ' : '> '}${pc.toString(16).toUpperCase().padStart(4, '0')}  ${op.name === '???' ? '.byte' : op.name} ${arg}`);
    pc += size;
  }
  ddtList.textContent = out.slice(0, 12).join('\n');
}
// the panel, the PDP-10's from the start and the same size throughout: what DDT does while BASIC
// runs, and the instructions and the buttons once it has stopped it
const ddtState = root.querySelector('.ms-ddt-state'), ddtTitle = root.querySelector('.ms-ddt-title'), ddtButtons = root.querySelector('.ms-ddt-buttons');
function ddtShow() {
  const pdp = kind === 'pdp10';
  strip.classList.toggle('with-ddt', pdp);
  ddtList.hidden = ddtTitle.hidden = ddtButtons.hidden = !pdp;
  ddtState.hidden = !m.paused; help.hidden = m.paused;
  ddtState.textContent = "BASIC is paused, about to do its next statement. Step through it, an instruction at a time, under the names in Microsoft's source.";
  for (const b of ddtButtons.querySelectorAll('button')) b.disabled = !m.paused;
  if (m.paused) listing(); else ddtList.textContent = pdp ? 'DDT stops BASIC\nhere, to be stepped\nthrough under the\nnames in Microsoft\'s\nsource.' : '';
}
let wasPaused = false;
ddtShow();

// a frame: a sixtieth of a second of the machine, about 16,667 cycles, and its clock
function frame() {
  m.run(16667);
  if (m.paused !== wasPaused) { wasPaused = m.paused; ddtShow(); }
  m.tick();
  panel.show({ ...chip.frame(), paused: m.paused });
  // (outside the machine, which says nothing while it tests its memory)
  const t = m.testing() ? 'BASIC is testing its memory, a byte at a time…' : '';
  if (testing.textContent !== t) testing.textContent = t;
  blink++;
  draw();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// the keys: typed on the canvas, or into the box beneath it (a phone's keyboard); the letters
// in capitals, as the machines took them, and Backspace the terminal's _
const send = text => m.type(kind === 'pet' ? text : text.toUpperCase());
function key(e) {
  if (e.metaKey || (e.ctrlKey && e.key !== 'c')) return;
  if (e.key === 'Escape' || (e.ctrlKey && e.key === 'c')) { m.stop(); e.preventDefault(); return; }
  // (the PET's cursor keys and HOME, Shift+Home its CLR, for its screen editing)
  const PET = { ArrowUp: 145, ArrowDown: 17, ArrowLeft: 157, ArrowRight: 29, Home: e.shiftKey ? 147 : 19 };
  if (kind === 'pet' && e.key in PET) { m.key(PET[e.key]); e.preventDefault(); return; }
  if (e.key === 'Enter') send('\n');
  else if (e.key === 'Backspace') send('\b');
  else if (e.key.length === 1) send(e.key);
  else return;
  e.preventDefault();
}
canvas.addEventListener('keydown', key);
keys.addEventListener('keydown', key);
keys.addEventListener('input', () => { if (keys.value) { send(keys.value); keys.value = ''; } });
for (const el of [canvas, keys]) el.addEventListener('paste', e => { send(e.clipboardData.getData('text').replace(/\r\n?/g, '\n')); e.preventDefault(); });
canvas.addEventListener('pointerdown', () => { if (matchMedia('(pointer: coarse)').matches) keys.focus(); });
root.addEventListener('click', e => {
  const b = e.target.closest('button'); if (!b) return;
  if (b.dataset.ddt) {
    if (b.dataset.ddt === 'step') m.step();
    else if (b.dataset.ddt === 'statement') { m.step(); for (let i = 0; i < 200000 && !m.label(m.cpu.pc)?.split(', ').includes('NEWSTT'); i++) m.step(); }
    else m.resume();
    ddtShow();
    canvas.focus();
    return;
  }
  if (b.dataset.kind) { start(b.dataset.kind).then(() => { wasPaused = false; ddtShow(); }); }
  else if (b.dataset.do === 'stop') m.stop();
  else if (b.dataset.do === 'reset') m.reset();
  else if (b.dataset.do === 'restart') start(kind);
  canvas.focus();
});
canvas.focus();

// A program as BASIC keeps it (./program.js): where the page has a place for it, lines typed and
// their bytes in memory printed, each part said in words, on printout paper; the build that keeps
// them started at the first, then kept
const kept2 = document.getElementById('ms-program');
if (kept2) {
  const { keeper } = await import('./program.js');
  kept2.innerHTML = `<p><textarea rows="3" cols="32" spellcheck="false" autocapitalize="characters" aria-label="Lines of BASIC">10 PRINT "HELLO"\n20 GOTO 10</textarea></p>
<p><button type="button" class="ms-show">Show its bytes</button></p>
<figure class="code" hidden><pre><code></code></pre><figcaption>The program's bytes in Commodore's build, from where it starts, $0401: each part's address, its bytes, and what they are.</figcaption></figure>`;
  const area = kept2.querySelector('textarea'), out = kept2.querySelector('figure'), code = out.querySelector('code');
  const hex = (n, w) => '$' + n.toString(16).toUpperCase().padStart(w, '0');
  const WHAT = { token: s => `${s}: a keyword, one byte`, text: s => `as typed: ${s}`, next: s => s, number: s => s, end: s => s, stop: s => s };
  let k = null;
  kept2.querySelector('.ms-show').addEventListener('click', () => {
    k ??= keeper(source);
    const rows = k.keep(area.value.toUpperCase());
    const lines = rows.map(r => `${hex(r.at, 4)}  ${r.bytes.map(b => hex(b, 2)).join(' ').padEnd(31)}  ${WHAT[r.what](r.says)}`);
    code.innerHTML = lines.map(l => `<span>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</span>`).join('');
    out.hidden = false;
  });
}
